import { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Loader2, Search, FileText, ChevronLeft, ChevronRight, Download, Clock, Home, User, Volume2, Star, PhoneIncoming, PhoneOutgoing, RefreshCw, Sparkles } from 'lucide-react';
import { api, type LlamadasListado, type LlamadaDetalle, type LlamadasBusqueda, type TurnoLlamada, type Sentimiento, type LlamadaResumida } from '@/lib/api';
import { ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, ReferenceLine, Tooltip, CartesianGrid } from 'recharts';
import { useChartTheme } from '@/components/aperturas/shared';
import Modal from '@/components/ui/Modal';
import BotonLlamar from '@/components/centralita/BotonLlamar';
import CampoEditable from '@/components/llamadas/CampoEditable';
import ProcesarLead from '@/components/llamadas/ProcesarLead';
import { toCsv } from '@/lib/utils';
import { formatDuracion } from './LlamadasMetricasPage';
import type { LlamadasContext } from './LlamadasLayout';

const PAGE_SIZE = 25;

/** Seconds into the call as mm:ss, to locate a line in the recording. */
function marca(segundos: number): string {
  const m = Math.floor(segundos / 60);
  const s = Math.floor(segundos % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** Faces and colours per sentiment, shared by the curve and the turn bubbles. */
export const SENTIMIENTO: Record<Sentimiento, { cara: string; texto: string; caja: string; etiqueta: string }> = {
  POSITIVE: { cara: '☺', texto: 'positivo', caja: 'bg-emerald-50 border-emerald-300', etiqueta: 'text-emerald-700' },
  NEGATIVE: { cara: '☹', texto: 'negativo', caja: 'bg-rose-50 border-rose-300', etiqueta: 'text-rose-700' },
  MIXED:    { cara: '◑', texto: 'mixto',    caja: 'bg-amber-50 border-amber-300', etiqueta: 'text-amber-700' },
  NEUTRAL:  { cara: '·', texto: 'neutro',   caja: 'bg-amber-50 border-amber-300', etiqueta: 'text-amber-700' },
};

const VALOR_SENTIMIENTO: Record<Sentimiento, number> = { POSITIVE: 1, NEGATIVE: -1, MIXED: 0, NEUTRAL: 0 };

/**
 * The customer's sentiment through the call, as a smoothed line from −5 to +5.
 *
 * A rolling window rather than raw per-turn values: single turns swing between
 * −1 and +1 constantly and the raw series is unreadable. What matters is where
 * the conversation *trends* sour, which is what a supervisor would go listen to.
 */
/**
 * Añade el punto exacto en el que la curva cruza el cero.
 *
 * Hace falta para poder pintar por separado lo positivo y lo negativo sin que
 * quede un escalón: sin este punto, el tramo que va de un valor positivo al
 * siguiente negativo se repartiría entre las dos mitades por donde cayeran las
 * muestras, no por donde de verdad cambia el ánimo.
 */
function conCruces(serie: { t: number; v: number }[]): { t: number; v: number }[] {
  const salida: { t: number; v: number }[] = [];
  serie.forEach((a, i) => {
    salida.push(a);
    const b = serie[i + 1];
    if (!b) return;
    if ((a.v > 0 && b.v < 0) || (a.v < 0 && b.v > 0)) {
      const fraccion = a.v / (a.v - b.v);
      salida.push({ t: Math.round(a.t + fraccion * (b.t - a.t)), v: 0 });
    }
  });
  return salida;
}

export function CurvaSentimiento({
  puntos,
  duracion,
}: {
  puntos: { t: number; s: Sentimiento }[];
  duracion: number;
}) {
  const chart = useChartTheme();
  const VENTANA = 60;
  const PASOS = 48;

  const serie: { t: number; v: number }[] = [];
  let ultimo = 0;
  for (let i = 0; i <= PASOS; i++) {
    const centro = (i * duracion) / PASOS;
    const cerca = puntos.filter((p) => Math.abs(p.t - centro) <= VENTANA / 2);
    if (cerca.length) {
      ultimo = (cerca.reduce((sum, p) => sum + VALOR_SENTIMIENTO[p.s], 0) / cerca.length) * 5;
    }
    serie.push({ t: Math.round(centro), v: Math.round(ultimo * 100) / 100 });
  }

  // Dos series a partir de una: cada tramo va entero a su lado del cero, y el
  // punto de cruce las cose sin escalón.
  const datos = conCruces(serie).map((p) => ({
    ...p,
    arriba: Math.max(0, p.v),
    abajo: Math.min(0, p.v),
  }));

  const negativos = puntos.filter((p) => p.s === 'NEGATIVE');
  const marca = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

  return (
    <div>
      <p className="label mb-1.5">Cómo se sintió durante la llamada</p>
      <ResponsiveContainer width="100%" height={130}>
        <ComposedChart data={datos} margin={{ top: 4, right: 6, bottom: 0, left: -28 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} vertical={false} />
          <XAxis dataKey="t" tickFormatter={marca} stroke={chart.axis} fontSize={10} minTickGap={40} />
          <YAxis domain={[-5, 5]} ticks={[-5, 0, 5]} stroke={chart.axis} fontSize={10} />
          <ReferenceLine y={0} stroke={chart.axis} strokeWidth={1} />
          <Tooltip
            cursor={{ stroke: chart.axis }}
            labelFormatter={(t) => marca(Number(t))}
            formatter={(v: any) => [Number(v).toFixed(1), 'sentimiento']}
          />
          {/* Un relleno por lado en vez de un degradado partido por la mitad.
              El degradado repartía sus colores sobre la caja del área pintada,
              no sobre el eje, así que en una llamada que nunca sube de cero esa
              caja va de 0 al mínimo y su mitad superior salía verde: verde por
              debajo del cero. Con dos series el color no puede equivocarse de
              lado, porque cada una solo existe en el suyo.
              `linear` y no `monotone`: una curva suavizada se pasa de largo
              entre puntos y volvería a teñir el lado contrario. */}
          <Area type="linear" dataKey="arriba" stroke="none" fill="#059669" fillOpacity={0.18}
                isAnimationActive={false} tooltipType="none" />
          <Area type="linear" dataKey="abajo" stroke="none" fill="#e11d48" fillOpacity={0.18}
                isAnimationActive={false} tooltipType="none" />
          <Line type="linear" dataKey="v" stroke={chart.bar} strokeWidth={2} dot={false}
                isAnimationActive={false} />
          {negativos.map((p) => (
            <ReferenceLine key={p.t} x={Math.round(p.t)} stroke="#e11d48" strokeDasharray="2 3" strokeWidth={1} />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
      {negativos.length > 0 && (
        <p className="text-xs text-slate-400 mt-1">
          {negativos.length === 1 ? 'Momento negativo' : 'Momentos negativos'} en {negativos.slice(0, 6).map((p) => marca(p.t)).join(', ')}
          {negativos.length > 6 ? '…' : ''}
        </p>
      )}
    </div>
  );
}

/**
 * Recovers the turns from a transcript saved before the structured `dialogue`
 * field existed.
 *
 * Those calls were already transcribed per speaker — the text carries
 * `[m:ss] Huésped: …` markers — but the array never got stored, so without this
 * they render as a wall of text until the backfill reaches them again. A
 * transcript with no markers really is one block and is left alone.
 */
export function turnosDesdeTexto(texto: string): TurnoLlamada[] | null {
  const turnos: TurnoLlamada[] = [];
  for (const linea of texto.split('\n')) {
    const m = linea.match(/^\[(\d+):(\d{2})\]\s*(Hu[ée]sped|Sereno)\s*:\s*(.+)$/i);
    if (!m) continue;
    turnos.push({
      t: Number(m[1]) * 60 + Number(m[2]),
      quien: m[3].toLowerCase().startsWith('sereno') ? 'sereno' : 'huesped',
      texto: m[4].trim(),
    });
  }
  return turnos.length ? turnos : null;
}

/**
 * The conversation, one block per speaker turn.
 *
 * Consecutive lines from the same speaker are merged: Whisper cuts on pauses,
 * so a single sentence often arrives split in three, and one block per fragment
 * would be as hard to follow as the wall of text this replaces.
 */
export function Dialogo({ turnos, sentimientos }: { turnos: TurnoLlamada[]; sentimientos?: Map<number, Sentimiento> }) {
  // Consecutive turns from the same speaker read as one paragraph, but their
  // original indices are kept so each block can find its sentiment.
  const bloques: { quien: TurnoLlamada['quien']; t: number; textos: string[]; indices: number[] }[] = [];
  turnos.forEach((turno, i) => {
    const ultimo = bloques[bloques.length - 1];
    if (ultimo && ultimo.quien === turno.quien) {
      ultimo.textos.push(turno.texto);
      ultimo.indices.push(i);
    } else {
      bloques.push({ quien: turno.quien, t: turno.t, textos: [turno.texto], indices: [i] });
    }
  });

  // A merged block takes the worst sentiment it contains: a bad moment should not
  // disappear just because the next sentence was calm.
  const ORDEN: Sentimiento[] = ['NEGATIVE', 'MIXED', 'POSITIVE', 'NEUTRAL'];
  const animoDe = (indices: number[]): Sentimiento | undefined => {
    if (!sentimientos) return undefined;
    const presentes = indices.map((i) => sentimientos.get(i)).filter(Boolean) as Sentimiento[];
    return ORDEN.find((s) => presentes.includes(s));
  };

  return (
    <div className="space-y-2.5">
      {bloques.map((b, i) => {
        const esHuesped = b.quien === 'huesped';
        // Two colour families rather than two shades of one: the eye should catch
        // who is speaking without having to read the label. On the guest's side the
        // sentiment overrides the colour, so a bad moment stands out while reading.
        const animo = esHuesped ? animoDe(b.indices) : undefined;
        const tono = animo && animo !== 'NEUTRAL' ? SENTIMIENTO[animo] : null;
        const caja = tono ? tono.caja : esHuesped ? 'bg-amber-50 border-amber-300' : 'bg-blue-50 border-blue-200';
        const etiqueta = tono ? tono.etiqueta : esHuesped ? 'text-amber-700' : 'text-blue-700';
        return (
          <div key={i} className="flex gap-2.5 items-start">
            <span className="text-[11px] text-slate-400 tabular-nums pt-2 w-10 shrink-0 text-right">
              {marca(b.t)}
            </span>
            <div className={`flex-1 rounded-lg border-l-[3px] px-3 py-2 ${caja}`}>
              <p className={`text-[11px] font-semibold uppercase tracking-wide mb-1 ${etiqueta}`}>
                {tono && <span className="mr-1 normal-case">{tono.cara}</span>}
                {esHuesped ? 'Huésped' : 'Sereno'}
                {tono && <span className="ml-1 font-normal normal-case tracking-normal">· {tono.texto}</span>}
              </p>
              <p className="text-sm text-slate-700 dark:text-slate-100 leading-relaxed">
                {b.textos.join(' ')}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** UTC instant -> the date and time a person in Madrid would name. */
const MESES_CORTOS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

/**
 * '16:52 · 22 Ago 26' en vez de '22/08/2026, 16:52'.
 *
 * La hora va primero porque al recorrer un listado casi siempre se compara
 * dentro del mismo día, y el mes en letra evita el titubeo de leer 08/07 sin
 * saber si es julio o agosto.
 */
export function enMadrid(iso: string): string {
  const partes = new Date(iso).toLocaleString('sv-SE', { timeZone: 'Europe/Madrid' }); // 2026-08-22 16:52:00
  const [fecha, hora] = partes.split(' ');
  const [anio, mes, dia] = fecha.split('-');
  return `${hora.slice(0, 5)} · ${Number(dia)} ${MESES_CORTOS[Number(mes) - 1]} ${anio.slice(2)}`;
}

/**
 * Marca o desmarca una llamada.
 *
 * Cambia de estado antes de que responda el servidor y se revierte si falla:
 * la marca es un gesto de una sola pulsación y esperar el viaje de ida y vuelta
 * hace dudar de si se ha registrado.
 */
/**
 * De qué lado vino la llamada.
 *
 * Un icono y no una palabra porque va repetido en cada fila: en un listado
 * donde casi todo es entrante, lo que hay que poder localizar de un vistazo es
 * la excepción. Las salientes son las que hacemos nosotros desde la centralita.
 */
export function Direccion({ valor }: { valor?: 'entrante' | 'saliente' | null }) {
  const saliente = valor === 'saliente';
  const Icono = saliente ? PhoneOutgoing : PhoneIncoming;
  return (
    <span title={saliente ? 'La hicimos nosotros' : 'Nos llamaron'} className="inline-flex">
      <Icono size={13} className={saliente ? 'text-sky-600' : 'text-slate-400'} />
    </span>
  );
}

export function BotonGuardar({
  guardada,
  onCambiar,
  tamano = 16,
}: {
  guardada: boolean;
  onCambiar: (nueva: boolean) => void | Promise<void>;
  tamano?: number;
}) {
  const [ocupado, setOcupado] = useState(false);
  return (
    <button
      type="button"
      disabled={ocupado}
      aria-pressed={guardada}
      title={guardada ? 'Quitar de guardadas' : 'Guardar esta llamada'}
      onClick={async (e) => {
        e.stopPropagation();
        setOcupado(true);
        try {
          await onCambiar(!guardada);
        } finally {
          setOcupado(false);
        }
      }}
      className={`grid place-items-center h-9 w-9 rounded-md transition-colors disabled:opacity-50 ${
        guardada
          ? 'text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-500/10'
          : 'text-slate-300 hover:text-slate-500 hover:bg-slate-100 dark:text-slate-600 dark:hover:text-slate-400 dark:hover:bg-white/5'
      }`}
    >
      <Star size={tamano} fill={guardada ? 'currentColor' : 'none'} />
    </button>
  );
}

/**
 * Una llamada en pantalla estrecha.
 *
 * La tabla de escritorio no cabe en un móvil: seis columnas quedan en cinco
 * caracteres cada una o fuerzan a desplazar de lado, y ninguna de las dos cosas
 * se lee. Aquí la misma información va apilada, con lo que identifica la
 * llamada —hora y quién— arriba y lo accesorio debajo.
 */
export function TarjetaLlamada({
  ll,
  onAbrir,
  onGuardar,
  onCorregir,
  onRestaurar,
  pie,
}: {
  ll: Omit<LlamadaResumida, 'transcriptStatus'>;
  onAbrir: () => void;
  onGuardar?: (nueva: boolean) => void | Promise<void>;
  onCorregir?: (campos: { guestName?: string; listingNickname?: string }) => Promise<void>;
  onRestaurar?: () => Promise<void>;
  pie?: React.ReactNode;
}) {
  return (
    <div className="card p-3 flex items-start gap-2">
      <button onClick={onAbrir} className="flex-1 min-w-0 text-left">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-sm font-medium text-slate-800 dark:text-slate-100 whitespace-nowrap inline-flex items-center gap-1.5">
            <Direccion valor={ll.direccion} />
            {enMadrid(ll.startedAt)}
          </span>
          <span className="text-xs text-slate-500 tabular-nums shrink-0">{formatDuracion(ll.durationSec)}</span>
        </div>
        <p className="text-sm text-slate-600 dark:text-slate-300 mt-1 truncate">
          <CampoEditable
            confirmado={ll.guestName}
            oido={ll.nombreDicho}
            corregido={ll.corregido?.guestName}
            ficha={ll.fichaHuesped}
            etiqueta="Editar el huésped"
            onGuardar={(v) => onCorregir?.({ guestName: v }) ?? Promise.resolve()}
            onRestaurar={onRestaurar}
          />
          {ll.phone && <span className="text-slate-400"> · {ll.phone}</span>}
        </p>
        <p className="text-xs text-slate-400 truncate mt-0.5">
          <CampoEditable
            confirmado={ll.listingNickname}
            oido={ll.alojamientoDicho}
            corregido={ll.corregido?.listingNickname}
            vacio="sin alojamiento"
            etiqueta="Editar el alojamiento"
            onGuardar={(v) => onCorregir?.({ listingNickname: v }) ?? Promise.resolve()}
            onRestaurar={onRestaurar}
          />
        </p>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1.5">
          {ll.categoria && <span className="badge text-[11px]">{ll.categoria.replace(/_/g, ' ')}</span>}
          {ll.sentimientoGlobal && (
            <span className={`text-[11px] ${SENTIMIENTO[ll.sentimientoGlobal].etiqueta}`}>
              {SENTIMIENTO[ll.sentimientoGlobal].cara} {SENTIMIENTO[ll.sentimientoGlobal].texto}
            </span>
          )}
          {ll.resuelto !== null && ll.resuelto !== undefined && (
            <span className={`text-[11px] ${ll.resuelto ? 'text-emerald-700' : 'text-rose-700'}`}>
              {ll.resuelto ? 'resuelta' : 'sin resolver'}
            </span>
          )}
        </div>
        {pie}
      </button>
      <div className="flex flex-col items-center gap-0.5 shrink-0">
        {onGuardar && <BotonGuardar guardada={Boolean(ll.guardada)} onCambiar={onGuardar} />}
        <BotonLlamar numero={ll.phone} nombre={ll.guestName} alojamiento={ll.listingNickname} compacto />
      </div>
    </div>
  );
}

export default function LlamadasListaPage() {
  const { from, to } = useOutletContext<LlamadasContext>();
  const [busqueda, setBusqueda] = useState('');
  const [enTranscripciones, setEnTranscripciones] = useState(false);
  const [direccion, setDireccion] = useState<'' | 'entrante' | 'saliente'>('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<LlamadasListado | null>(null);
  const [resultados, setResultados] = useState<LlamadasBusqueda | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detalle, setDetalle] = useState<LlamadaDetalle | null>(null);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const [procesandoLead, setProcesandoLead] = useState(false);

  const cargar = useCallback(async ({ refrescar = false } = {}) => {
    setLoading(true);
    setError(null);
    try {
      if (enTranscripciones) {
        if (busqueda.trim().length < 3) {
          setError('Escribe al menos 3 caracteres para buscar dentro de las transcripciones.');
          setResultados(null);
          return;
        }
        setResultados(await api.buscarEnTranscripciones(busqueda.trim()));
        setData(null);
      } else {
        setResultados(null);
        setData(await api.getLlamadas({
          from, to, q: busqueda.trim() || undefined, page, pageSize: PAGE_SIZE,
          direccion: direccion || undefined,
          refrescar,
        }));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error cargando las llamadas');
    } finally {
      setLoading(false);
    }
  }, [from, to, page, busqueda, enTranscripciones, direccion]);

  // The text search runs on submit, not on every keystroke: searching inside
  // transcripts scans the container and is not something to fire per letter.
  useEffect(() => { if (!busqueda || !enTranscripciones) cargar(); }, [from, to, page, enTranscripciones, direccion]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Marca o desmarca, actualizando la lista en el sitio sin recargarla entera. */
  const guardar = async (id: string, nueva: boolean) => {
    const aplicar = (v: boolean) => {
      setData((d) => (d ? { ...d, items: d.items.map((i) => (i.id === id ? { ...i, guardada: v } : i)) } : d));
      setDetalle((d) => (d && d.id === id ? { ...d, guardada: v } : d));
    };
    aplicar(nueva);
    try {
      await api.marcarLlamada(id, nueva);
    } catch (e) {
      aplicar(!nueva);
      setError(e instanceof Error ? e.message : 'No se pudo cambiar la marca de guardada');
    }
  };

  /**
   * Corrige el huésped o el alojamiento de una llamada.
   *
   * Se aplica en el sitio sin recargar la lista: quien corrige suele hacerlo
   * de varias seguidas mientras repasa, y recargar entera perdería la página y
   * el foco en cada una.
   */
  const corregir = async (id: string, campos: { guestName?: string; listingNickname?: string }) => {
    const aplicar = (valores: { guestName?: string | null; listingNickname?: string | null }, marca: boolean) => {
      const parche = (i: LlamadaResumida) => {
        if (i.id !== id) return i;
        const corr = { ...(i.corregido ?? { guestName: false, listingNickname: false }) };
        if (valores.guestName !== undefined) corr.guestName = marca;
        if (valores.listingNickname !== undefined) corr.listingNickname = marca;
        return { ...i, ...valores, corregido: corr };
      };
      setData((d) => (d ? { ...d, items: d.items.map(parche) } : d));
      setDetalle((d) => (d && d.id === id ? (parche(d) as LlamadaDetalle) : d));
    };
    try {
      const r = await api.corregirLlamada(id, campos);
      // Se releen del servidor los dos campos: así el estado local no se separa
      // de lo guardado si el servidor recortó o normalizó algo.
      aplicar(
        {
          ...(campos.guestName !== undefined ? { guestName: r.guestName } : {}),
          ...(campos.listingNickname !== undefined ? { listingNickname: r.listingNickname } : {}),
        },
        true,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo guardar la corrección');
    }
  };

  /** Quita todas las correcciones de una llamada y la recarga desde el origen. */
  const restaurar = async (id: string) => {
    try {
      await api.quitarCorreccionLlamada(id);
      const fresca = await api.getLlamada(id);
      setData((d) => (d ? { ...d, items: d.items.map((i) => (i.id === id ? { ...i, ...fresca } : i)) } : d));
      setDetalle((d) => (d && d.id === id ? fresca : d));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo deshacer la corrección');
    }
  };

  const abrirDetalle = async (id: string) => {
    setCargandoDetalle(true);
    try {
      setDetalle(await api.getLlamada(id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cargar la llamada');
    } finally {
      setCargandoDetalle(false);
    }
  };

  const exportar = () => {
    const filas = resultados?.items ?? data?.items ?? [];
    const csv = toCsv([
      ['Fecha', 'Teléfono', 'Huésped', 'Alojamiento', 'Duración (s)'],
      ...filas.map((f) => [enMadrid(f.startedAt), f.phone ?? '', f.guestName ?? '', f.listingNickname ?? '', f.durationSec ?? '']),
    ]);
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `llamadas-${from}_${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const items = resultados?.items ?? data?.items ?? [];
  const totalPaginas = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <div className="space-y-4">
      <form
        onSubmit={(e) => { e.preventDefault(); setPage(1); cargar(); }}
        className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 sm:gap-3"
      >
        <div className="relative flex-1 sm:min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <input
            className="input pl-9"
            placeholder={enTranscripciones ? 'Buscar en lo que se dijo…' : 'Teléfono, huésped o alojamiento…'}
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2">
          <button type="submit" className="btn-primary flex-1 sm:flex-none min-h-10" disabled={loading}>
            {loading ? <Loader2 className="animate-spin mx-auto" size={16} /> : 'Buscar'}
          </button>
          {/* Actualizar sin recargar la página: la lista cambia sola conforme
              entran llamadas y se transcriben las que estaban en cola, y
              recargar entera perdería la búsqueda y la página en la que estabas. */}
          <button
            type="button"
            onClick={() => cargar({ refrescar: true })}
            disabled={loading}
            title="Volver a cargar la lista"
            aria-label="Actualizar"
            className="btn-secondary flex items-center justify-center min-h-10 w-10 shrink-0 disabled:opacity-40"
          >
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
          </button>
          {items.length > 0 && (
            <button
              type="button"
              onClick={exportar}
              className="btn-secondary flex items-center justify-center gap-1.5 min-h-10 px-3"
            >
              <Download size={15} /> <span className="sm:inline">CSV</span>
            </button>
          )}
        </div>
        {!enTranscripciones && (
          <div className="flex gap-1 order-last sm:order-none" role="group" aria-label="Filtrar por dirección">
            {([['', 'Todas'], ['entrante', 'Entrantes'], ['saliente', 'Salientes']] as const).map(([v, etiqueta]) => (
              <button
                key={v}
                type="button"
                onClick={() => { setDireccion(v); setPage(1); }}
                className={`min-h-10 px-3 text-sm rounded-md whitespace-nowrap transition-colors ${
                  direccion === v
                    ? 'bg-sereno-dark text-white'
                    : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5'
                }`}
              >{etiqueta}</button>
            ))}
          </div>
        )}
        <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 order-last sm:order-none">
          <input
            type="checkbox"
            className="h-4 w-4"
            checked={enTranscripciones}
            onChange={(e) => { setEnTranscripciones(e.target.checked); setPage(1); }}
          />
          Buscar en transcripciones
        </label>
      </form>

      {error && <div className="card py-3 text-sm text-amber-700 dark:text-amber-300">{error}</div>}

      {loading && !items.length ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-slate-400" size={28} /></div>
      ) : !items.length ? (
        <div className="card text-center py-12 text-slate-500">
          {busqueda ? 'Ninguna llamada coincide con la búsqueda.' : 'No hay llamadas en este periodo.'}
        </div>
      ) : (
        <>
          {/* Móvil: una tarjeta por llamada. La tabla de abajo no cabe en 375px. */}
          <div className="sm:hidden space-y-2">
            {items.map((ll) => (
              <TarjetaLlamada
                key={ll.id}
                ll={ll}
                onAbrir={() => abrirDetalle(ll.id)}
                onGuardar={(nueva) => guardar(ll.id, nueva)}
                onCorregir={(campos) => corregir(ll.id, campos)}
                onRestaurar={() => restaurar(ll.id)}
              />
            ))}
          </div>

          {/* Escritorio: la tabla, que compara mejor fila contra fila. */}
          <div className="hidden sm:block card p-0 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-white/5 text-slate-500">
                <tr>
                  <th className="w-10 px-2 py-2.5"></th>
                  <th className="text-left font-medium px-4 py-2.5">Fecha</th>
                  <th className="text-left font-medium px-4 py-2.5">Teléfono</th>
                  <th className="text-left font-medium px-4 py-2.5">Huésped</th>
                  <th className="text-left font-medium px-4 py-2.5 hidden md:table-cell">Alojamiento</th>
                  <th className="text-right font-medium px-4 py-2.5">Duración</th>
                  <th className="w-12 px-2 py-2.5"></th>
                  <th className="px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((ll) => (
                  <tr key={ll.id} className="border-t border-slate-100 dark:border-white/5">
                    <td className="px-2 py-1.5">
                      <BotonGuardar guardada={Boolean(ll.guardada)} onCambiar={(n) => guardar(ll.id, n)} tamano={15} />
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <span className="inline-flex items-center gap-1.5">
                        <Direccion valor={ll.direccion} />
                        {enMadrid(ll.startedAt)}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap">{ll.phone ?? '—'}</td>
                    <td className="px-4 py-2.5">
                      <CampoEditable
                        confirmado={ll.guestName}
                        oido={ll.nombreDicho}
                        corregido={ll.corregido?.guestName}
                        ficha={ll.fichaHuesped}
                        etiqueta="Editar el huésped"
                        onGuardar={(v) => corregir(ll.id, { guestName: v })}
                        onRestaurar={() => restaurar(ll.id)}
                      />
                    </td>
                    <td className="px-4 py-2.5 hidden md:table-cell">
                      <CampoEditable
                        confirmado={ll.listingNickname}
                        oido={ll.alojamientoDicho}
                        corregido={ll.corregido?.listingNickname}
                        vacio="—"
                        etiqueta="Editar el alojamiento"
                        onGuardar={(v) => corregir(ll.id, { listingNickname: v })}
                        onRestaurar={() => restaurar(ll.id)}
                      />
                    </td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">{formatDuracion(ll.durationSec)}</td>
                    <td className="px-2 py-1.5">
                      <BotonLlamar numero={ll.phone} nombre={ll.guestName} alojamiento={ll.listingNickname} compacto />
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <button onClick={() => abrirDetalle(ll.id)} className="btn-ghost text-xs flex items-center gap-1 ml-auto">
                        <FileText size={13} /> Ver
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {resultados && (
            <div className="space-y-2">
              {resultados.items.map((r) => (
                <button
                  key={r.id}
                  onClick={() => abrirDetalle(r.id)}
                  className="card w-full text-left py-3 hover:bg-slate-50 dark:hover:bg-white/5"
                >
                  <p className="text-xs text-slate-400 mb-1">{enMadrid(r.startedAt)} · {r.guestName ?? 'sin identificar'}</p>
                  <p className="text-sm text-slate-600 dark:text-slate-300">{r.fragmento}</p>
                </button>
              ))}
            </div>
          )}

          {data && totalPaginas > 1 && (
            <div className="flex items-center justify-between gap-3 text-sm text-slate-500">
              <span className="min-w-0">
                {data.total.toLocaleString('es-ES')} llamadas
                <span className="hidden sm:inline"> · página {page} de {totalPaginas}</span>
                <span className="sm:hidden"> · {page}/{totalPaginas}</span>
              </span>
              <div className="flex gap-1 shrink-0">
                <button
                  className="btn-ghost h-10 w-10 grid place-items-center disabled:opacity-40"
                  aria-label="Página anterior"
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((p) => p - 1)}
                ><ChevronLeft size={16} /></button>
                <button
                  className="btn-ghost h-10 w-10 grid place-items-center disabled:opacity-40"
                  aria-label="Página siguiente"
                  disabled={page >= totalPaginas || loading}
                  onClick={() => setPage((p) => p + 1)}
                ><ChevronRight size={16} /></button>
              </div>
            </div>
          )}
        </>
      )}

      <Modal isOpen={Boolean(detalle) || cargandoDetalle} onClose={() => setDetalle(null)} title="Llamada" size="2xl">
        {cargandoDetalle || !detalle ? (
          <div className="flex justify-center py-10"><Loader2 className="animate-spin text-slate-400" size={24} /></div>
        ) : (
          <ContenidoLlamada
            detalle={detalle}
            onGuardar={(n) => guardar(detalle.id, n)}
            onCorregir={(campos) => corregir(detalle.id, campos)}
            onRestaurar={() => restaurar(detalle.id)}
            onProcesarLead={() => setProcesandoLead(true)}
          />
        )}
      </Modal>

      {procesandoLead && detalle && (
        <ProcesarLead
          llamada={detalle}
          onCerrar={() => setProcesandoLead(false)}
          onProcesada={(r) => setDetalle((d) => (d ? { ...d, lead: r.enlace } : d))}
        />
      )}
    </div>
  );
}

/** El panel de una llamada: grabación, datos y conversación. */
export function ContenidoLlamada({
  detalle,
  onGuardar,
  onCorregir,
  onRestaurar,
  onProcesarLead,
}: {
  detalle: LlamadaDetalle;
  onGuardar?: (nueva: boolean) => void | Promise<void>;
  onCorregir?: (campos: { guestName?: string; listingNickname?: string }) => Promise<void>;
  onRestaurar?: () => Promise<void>;
  /**
   * Volcar la llamada sobre la ficha de un lead de propietario. Opcional a
   * propósito: este panel también se usa en la ficha del huésped, y ahí un
   * botón para procesar la llamada como captación no significa nada.
   */
  onProcesarLead?: () => void;
}) {
  const turnos = detalle.dialogue?.length
    ? detalle.dialogue
    : detalle.transcript
      ? turnosDesdeTexto(detalle.transcript)
      : null;

  // The analysis references turns by their index in `dialogue`, so the map only
  // lines up when the turns came from there — not from a parsed transcript.
  const sentimientos = detalle.dialogue?.length && detalle.sentimientoPorTurno?.length
    ? new Map(detalle.sentimientoPorTurno.map((p) => [p.i, p.s]))
    : undefined;
  const puntosCurva = detalle.sentimientoPorTurno?.length ? detalle.sentimientoPorTurno : null;
  const tieneConversacion = Boolean(turnos?.length || detalle.transcript?.trim());

  return (
          <div className="space-y-4">
            {puntosCurva && detalle.durationSec ? (
              <CurvaSentimiento puntos={puntosCurva} duracion={detalle.durationSec} />
            ) : null}

            {detalle.audioKey && (
              <div>
                <p className="label mb-1.5 flex items-center gap-1.5"><Volume2 size={14} /> Grabación</p>
                {/* The browser talks to S3 directly through a redirect, so seeking
                    works without this app streaming the file. preload="none" keeps
                    the request from firing until someone presses play. */}
                <audio
                  controls
                  preload="none"
                  className="w-full"
                  src={`/api/llamadas/${encodeURIComponent(detalle.id)}/audio`}
                >
                  Tu navegador no puede reproducir audio.
                </audio>
              </div>
            )}

            <div className="flex items-start gap-2">
              <div className="flex-1 flex flex-col sm:flex-row sm:flex-wrap gap-x-6 gap-y-1.5 text-sm text-slate-600 dark:text-slate-300">
                <span className="flex items-center gap-1.5">
                  <Clock size={14} className="text-slate-400 shrink-0" />
                  <Direccion valor={detalle.direccion} /> {enMadrid(detalle.startedAt)}
                  <span className="text-slate-400">· {formatDuracion(detalle.durationSec)}</span>
                </span>
                <span className="flex items-center gap-1.5 min-w-0">
                  <User size={14} className="text-slate-400 shrink-0" />
                  <CampoEditable
                    confirmado={detalle.guestName}
                    oido={detalle.nombreDicho}
                    corregido={detalle.corregido?.guestName}
                    ficha={detalle.fichaHuesped}
                    etiqueta="Editar el huésped"
                    onGuardar={(v) => onCorregir?.({ guestName: v }) ?? Promise.resolve()}
                    onRestaurar={onRestaurar}
                  />
                  <span className="text-slate-400">· {detalle.phone ?? '—'}</span>
                </span>
                {/* Siempre visible, aunque esté vacío: es donde se corrige, y un
                    campo que solo aparece cuando ya tiene valor no se puede rellenar. */}
                <span className="flex items-center gap-1.5 min-w-0">
                  <Home size={14} className="text-slate-400 shrink-0" />
                  <CampoEditable
                    confirmado={detalle.listingNickname}
                    oido={detalle.alojamientoDicho}
                    corregido={detalle.corregido?.listingNickname}
                    vacio="sin alojamiento"
                    etiqueta="Editar el alojamiento"
                    onGuardar={(v) => onCorregir?.({ listingNickname: v }) ?? Promise.resolve()}
                    onRestaurar={onRestaurar}
                  />
                </span>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {onProcesarLead && (
                  <button
                    type="button"
                    onClick={onProcesarLead}
                    disabled={!tieneConversacion}
                    title={
                      tieneConversacion
                        ? detalle.lead
                          ? `Ya procesada para ${detalle.lead.leadNombre}`
                          : 'Volcar esta llamada en la ficha de un lead'
                        : 'Sin transcripción no hay nada que volcar'
                    }
                    className="btn-ghost text-xs flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Sparkles size={14} className={detalle.lead ? 'text-emerald-600' : undefined} />
                    <span className="hidden sm:inline">
                      {detalle.lead ? 'Procesada' : 'Procesar para Lead'}
                    </span>
                  </button>
                )}
                <BotonLlamar
                  numero={detalle.phone}
                  nombre={detalle.guestName}
                  alojamiento={detalle.listingNickname}
                />
                {onGuardar && (
                  <BotonGuardar guardada={Boolean(detalle.guardada)} onCambiar={onGuardar} tamano={18} />
                )}
              </div>
            </div>

            {(detalle.categoria || detalle.intencion) && (
              <div className="card py-2.5 px-3 bg-slate-50 dark:bg-white/5">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                  {detalle.categoria && (
                    <span className="badge">{detalle.categoria.replace(/_/g, ' ')}</span>
                  )}
                  {detalle.sentimientoGlobal && (
                    <span className={SENTIMIENTO[detalle.sentimientoGlobal].etiqueta}>
                      {SENTIMIENTO[detalle.sentimientoGlobal].cara} {SENTIMIENTO[detalle.sentimientoGlobal].texto}
                    </span>
                  )}
                  {detalle.resuelto !== null && (
                    <span className={detalle.resuelto ? 'text-emerald-700' : 'text-rose-700'}>
                      {detalle.resuelto ? 'resuelta' : 'sin resolver'}
                    </span>
                  )}
                </div>
                {detalle.intencion && (
                  <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">{detalle.intencion}</p>
                )}
              </div>
            )}

            {turnos ? (
              <div>
                <p className="label mb-2">Conversación</p>
                <Dialogo turnos={turnos} sentimientos={sentimientos} />
                <p className="text-xs text-slate-400 mt-3">
                  Transcrita automáticamente con Whisper; puede contener errores. Cada voz se graba en su
                  propia pista, así que quién habla es un dato de la grabación y no una estimación.
                </p>
              </div>
            ) : detalle.transcript ? (
              <div>
                <p className="label mb-1">Transcripción</p>
                <p className="text-sm whitespace-pre-wrap text-slate-700 dark:text-slate-200 leading-relaxed">
                  {detalle.transcript}
                </p>
                <p className="text-xs text-slate-400 mt-3">
                  Transcrita antes de que se separasen los interlocutores, así que va en un solo bloque.
                </p>
              </div>
            ) : (
              <p className="text-sm text-slate-500">
                {detalle.transcriptStatus === 'pending'
                  ? 'Esta llamada aún no se ha transcrito. Aparecerá en cuanto le llegue el turno.'
                  : detalle.transcriptStatus === 'no_audio'
                    ? 'No se conserva la grabación de esta llamada, así que no hay nada que transcribir.'
                    : 'No se pudo transcribir esta llamada.'}
              </p>
            )}
          </div>
  );
}
